import { useEffect, useState } from 'react'
import { FileSpreadsheet } from 'lucide-react'
import Button from './Button'
import ExcelUploadFields from './ExcelUploadFields'
import { FormBody } from './Form'
import Modal from './Modal'
import { excel as DEFAULTS } from './strings'

/**
 * "…from a file" — the sheet behind every excel import in the panel.
 *
 * Four buttons opened nothing at all: إضافة موضوع من ملف, رفع ملف الأسئلة, and
 * استيراد on both phishing lists. All four are the same transaction — read the
 * template, pick a file, post it — and the body was already built
 * (ExcelUploadFields), so they share one sheet rather than growing four.
 *
 * `columns` is optional: the users and groups imports state their column list,
 * the questions one only points at a template.
 */
/**
 * The wording is identical on all four, so it lives here rather than being
 * copied into four feature string files. A caller that needs different words —
 * the users import states its own columns and its own note — passes `strings`
 * and overrides only what differs.
 */
export default function ExcelImportModal({
  open,
  title,
  accept = '.xls,.xlsx,.csv',
  columns,
  templateHref,
  strings: overrides,
  onClose,
  /**
   * Receives the picked `File` ITSELF, not `{ file }`.
   *
   * Said here because two screens read it as an object and got `undefined`: the import
   * never left the browser and the sheet showed its generic failure, which looks exactly
   * like a server refusing the file. `GroupBulkModal` next door passes `{ file }`, which is
   * how the two conventions got mixed up in the first place.
   *
   * @type {(file: File) => void}
   */
  onSubmit,
}) {
  const strings = { ...DEFAULTS, ...overrides }
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setFile(null)
    setError('')
  }, [open])

  const submit = (event) => {
    event.preventDefault()
    if (!file) {
      setError(strings.fileRequired)
      return
    }
    onSubmit?.(file)
  }

  return (
    <Modal
      open={open}
      title={title}
      icon={FileSpreadsheet}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form="excel-import-form">
            {strings.save}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {strings.close}
          </Button>
        </>
      }
    >
      <form id="excel-import-form" onSubmit={submit} noValidate>
        <FormBody>
          <ExcelUploadFields
            id="excel-import-file"
            name="excel"
            accept={accept}
            columns={columns}
            templateHref={templateHref}
            error={error}
            strings={strings}
            onChange={(selected) => {
              setFile(selected)
              setError('')
            }}
          />
        </FormBody>
      </form>
    </Modal>
  )
}
